//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Actions
import {
  manageCreateEditManageVehicleStatus,
  manageVehicleStatusGetIcons,
  reset
} from "./redux/slice";

//Controls
import { Button, Form, Input, Modal, Select } from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager, NotificationContainer } from "Components/Notifications";
import { Alert, Grid } from "@mui/material";
import { hasEmojiValidation } from "Modules/manage/.globals/utils";
import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";


const PATH = `/assets/icons/`;


const CreateEditManageVehicleStatus = ({ refresh }) => {
  //States
  const [formData, setFormData] = useState({ name: null, vehicleStatusIcon: null });
  const [options, setOptions] = useState([]);

  //Language
  const { messages } = useIntl();

  //Form refs
  const createEditKeyFobFormRef = Form.useForm();

  //Redux
  const {
    vehicleStatus,
    loading,
    error,
    updateManageVehicleStatusResult,
    open,
    vehicleStatusIcons
  } = useSelector((state) => state.manageCreateEditManageVehicleStatusRedux);
  const dispatch = useDispatch();

  //Memos
  const title = useMemo(
    () =>
      vehicleStatus?.id
        ? `${messages["manage_createEditVehicleStatus_EditTitle"]}: "${vehicleStatus?.name}"`
        : messages["manage_createEditVehicleStatus_CreateTitle"],
    [vehicleStatus]
  );

  const nameFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );

  //Handlers and utils
  const handleupdateManageVehicleStatusResult = useCallback(() => {
    const name =
      formData.name != null ? String(formData.name).trim() : "";
    if (!name || !formData.vehicleStatusIcon) return NotificationManager.warning(
      messages["manage_createEditVehicleStatus_warning"],
      messages["warning"],
      3000,
      null,
      null,
      "warning"
    );
    const { vehicleStatusIcon } = formData;
    if(hasEmojiValidation(name)) return NotificationManager.warning(
      messages["manage_createEditVehicleStatus_warning_characters"],
      messages["warning"],
      3000,
      null,
      null,
      "warning"
    ); 
    dispatch(
      manageCreateEditManageVehicleStatus({
        name: vehicleStatus?.name === name ? null : name,
        iconId: vehicleStatusIcon?.id,
        id: vehicleStatus?.id
      })
    );
  }, [formData, vehicleStatus]);

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, [dispatch]);

  const handleSuccess = useCallback(() => {
    NotificationManager.success(
      vehicleStatus?.id ? messages["manage_createEditVehicleStatus_success_edit"] : messages["manage_createEditVehicleStatus_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }, []);

  //Effects
  useEffect(() => {
    if (!updateManageVehicleStatusResult?.id) return;
    handleSuccess();
  }, [updateManageVehicleStatusResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1010)) {
      errorMessage = messages["manage_createEditVehicleStatus_duplicatedVehicleStatusErrorMessage"];
    }
    NotificationManager.error(errorMessage, messages["error"], 4000, null, null, "error");
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!open) return;
    dispatch(manageVehicleStatusGetIcons(null));
  }, [open]);

  useEffect(() => {
    if (!open || !vehicleStatus) return;
    const { name, iconId } = vehicleStatus;
    const defaultData = {
      name,
      vehicleStatusIcon: vehicleStatusIcons?.find((icon) => icon?.id === iconId)
    };
    setFormData(defaultData);
    createEditKeyFobFormRef.setValues(defaultData);
  }, [vehicleStatus, open, vehicleStatusIcons]);

  useEffect(() => {
    if (vehicleStatusIcons && open) {

      var newOptions = []
      let optionSelected = '';
      vehicleStatusIcons?.forEach((statusIcon) => {
        let option = {
          id: statusIcon.id,
          name: <Grid
            container
            key={statusIcon.id}
            justifyContent="space-between"
          >
            {statusIcon.name} 
            <div style={{width:10}}></div>
            <img src={`${PATH}${statusIcon?.iconPath?.replace('../media/icons/map_', '')}`} />
          </Grid>
        }
        newOptions.push(option)
        if (vehicleStatus && statusIcon?.id == vehicleStatus.iconId) {
          optionSelected = option;
        }
      });

      setOptions(newOptions);
      setFormData({
        ...formData,
        vehicleStatusIcon: optionSelected,
        name: vehicleStatus?.name
      });
      createEditKeyFobFormRef.setValues({
        ...formData,
        vehicleStatusIcon: optionSelected,
        name: vehicleStatus?.name
      });

    }
  }, [vehicleStatusIcons, open]);
  

  return (
    <Modal
      title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditKeyFobFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={createEditKeyFobFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleupdateManageVehicleStatusResult()}
          onChangeValues={() => {}}
        >
          <Form.Item
            name="name"
            label={messages["manage_createEditVehicleStatus_NameInputLabel"]}
            rules={nameFieldRules}
          >
            <Input
              id="name"
              type="name"
              placeholder={messages["manage_createEditVehicleStatus_NameInputLabel"]}
              onChange={(value) => {
                setFormData({ ...formData, name: value });
              }}
              maxLength={20}
              isDisabled={vehicleStatus?.keyFobModelId === 2}
            />
          </Form.Item>
          <Form.Item
            label={messages["manage_createEditVehicleStatus_IconInputLabel"]}
            name={`vehicleStatusIcon`}
            rules={[
              {
                required: true,
                message: messages["required_field"]
              }
            ]}
          >
            <Select
              id="vehicleStatusIcon"
              placeholder={messages["manage_createEditVehicleStatus_IconInputLabel"]}
              loading={loading}
              options={options || vehicleStatusIcons || []}
              textField="name"
              valueField="id"
              value={formData.vehicleStatusIcon?.id ? formData.vehicleStatusIcon : null}
              onChange={(value) => {
                setFormData({ ...formData, vehicleStatusIcon: value });
              }}
              isClearable
              isSearchable
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageVehicleStatus;
